import { z } from "zod";

export enum WalletTypes {
  APPLICATION = "application",
  AFFILIATE = "affiliate",
  ORG_PROGRAM_FEE = "org_program_fee",
  ORG_LEGAL_FEE = "org_legal_fee",
  ORG_SERVICE_FEE = "org_service_fee",
  CREDITOR = "creditor",
  RECONCILIATION = "reconciliation",
}

export enum ReconciliationTypes {
  APPLICATION = "application",
  AFFILIATE = "affiliate",
  ORG_PROGRAM_FEE = "org_program_fee",
  ORG_LEGAL_FEE = "org_legal_fee",
  ORG_SERVICE_FEE = "org_service_fee",
  CREDITOR = "creditor",
}

export enum WalletStatus {
  ACTIVE = "active",
  LOCKED = "locked",
}

const objectId = z.string().min(1);

const BaseWalletSchema = z.object({
  wallet_type: z.nativeEnum(WalletTypes),
  wallet_status: z.nativeEnum(WalletStatus).default(WalletStatus.ACTIVE),
  org_code: z.string().optional(),
  org_id: objectId.optional(),
  currency: z.string().default("USD"),
  van: z.string().optional(),
});

const ApplicationWalletSchema = BaseWalletSchema.extend({
  wallet_type: z.literal(WalletTypes.APPLICATION),
  appkey: z.string().optional(),
  application_id: objectId,
});

const AffiliateWalletSchema = BaseWalletSchema.extend({
  wallet_type: z.literal(WalletTypes.AFFILIATE),
  user_id: objectId,
});

const OrgProgramFeeWalletSchema = BaseWalletSchema.extend({
  wallet_type: z.literal(WalletTypes.ORG_PROGRAM_FEE),
  org_code: z.string().optional(),
  org_id: objectId,
});

const OrgLegalFeeWalletSchema = BaseWalletSchema.extend({
  wallet_type: z.literal(WalletTypes.ORG_LEGAL_FEE),
  org_code: z.string().optional(),
  org_id: objectId,
});

const OrgServiceFeeWalletSchema = BaseWalletSchema.extend({
  wallet_type: z.literal(WalletTypes.ORG_SERVICE_FEE),
  org_code: z.string().optional(),
  org_id: objectId,
});

const CreditorFeeWalletSchema = BaseWalletSchema.extend({
  wallet_type: z.literal(WalletTypes.CREDITOR),
  creditor_id: objectId,
});

const ReconciliationWalletSchema = BaseWalletSchema.extend({
  wallet_type: z.literal(WalletTypes.RECONCILIATION),
  reference_type: z.nativeEnum(ReconciliationTypes),
  application_id: objectId.optional(),
  user_id: objectId.optional(),
  org_id: objectId.optional(),
  creditor_id: objectId.optional(),
  appkey: z.string().optional(),
  org_code: z.string().optional(),
});

export const WalletSchema = z.discriminatedUnion("wallet_type", [
  ApplicationWalletSchema,
  AffiliateWalletSchema,
  OrgProgramFeeWalletSchema,
  OrgLegalFeeWalletSchema,
  OrgServiceFeeWalletSchema,
  CreditorFeeWalletSchema,
  ReconciliationWalletSchema,
]);

export type WalletDto = z.infer<typeof WalletSchema> & { _id: string };
export type CreateWalletDto = z.input<typeof WalletSchema>;
